/**
 * Compras: por dónde entra la mercadería.
 *
 * El stock NO se escribe a mano en ninguna pantalla. Entra por aquí, con su
 * documento, su proveedor, su lote y su vencimiento — que es lo que después
 * permite rastrear un lote y calcular cuánto costó lo que se vendió.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  ChevronDown,
  ChevronRight,
  PackagePlus,
  Plus,
  Receipt,
  Trash2,
} from "lucide-react";
import {
  type Compra,
  type CompraCreate,
  type Producto,
  confirmarCompra,
  createCompra,
  createProveedor,
  listCompras,
  listProductos,
  listProveedores,
} from "../api/farmacia";
import { listSedes } from "../api/tenants";
import { DemandaNoCubiertaCard } from "../components/farmacia/DemandaNoCubiertaCard";
import { formatApiError } from "../api/client";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  Field,
  Input,
} from "../components/ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

interface LineaCompra {
  producto: Producto;
  numero_lote: string;
  fecha_vencimiento: string;
  cantidad: number;
  costo_unitario: number;
}

export function FarmaciaComprasPage() {
  const queryClient = useQueryClient();
  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const { data: compras = [], isLoading } = useQuery({
    queryKey: ["farmacia", "compras"],
    queryFn: () => listCompras(),
  });

  const confirmar = useMutation({
    mutationFn: (id: number) => confirmarCompra(id),
    onSuccess: () => {
      setOk("Compra confirmada: la mercadería ya está en el inventario.");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["farmacia"] });
    },
    onError: (e) => setError(formatApiError(e, "No se pudo confirmar.")),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Receipt className="h-6 w-6 text-[color:var(--color-link)]" />
            Compras
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Ingreso de mercadería. Es la única puerta por la que entra stock.
          </p>
        </div>
        <Button onClick={() => setNuevaOpen(true)}>
          <PackagePlus className="h-4 w-4" />
          Registrar ingreso
        </Button>
      </div>

      {error && <Alert>{error}</Alert>}
      {ok && <Alert variant="success">{ok}</Alert>}

      {/* Antes de la lista de compras, no después: esto es lo que hay que
          comprar, y lo de abajo es lo que ya se compró. */}
      <DemandaNoCubiertaCard />

      <Card>
        <CardBody className="p-0">
          {isLoading && (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
          )}
          {!isLoading && compras.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-slate-500">
              Sin compras registradas.
            </p>
          )}
          <ul className="divide-y divide-slate-100">
            {compras.map((c) => (
              <FilaCompra
                key={c.id}
                compra={c}
                onConfirmar={() => confirmar.mutate(c.id)}
              />
            ))}
          </ul>
        </CardBody>
      </Card>

      {nuevaOpen && (
        <NuevaCompraDialog
          onClose={() => setNuevaOpen(false)}
          onDone={(msg) => {
            setNuevaOpen(false);
            setOk(msg);
            setError(null);
            queryClient.invalidateQueries({ queryKey: ["farmacia"] });
          }}
          onError={(m) => {
            setError(m);
            setOk(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * Una compra: cabecera legible y el detalle en tabla, plegado.
 *
 * Un ingreso de 17 productos escrito en líneas corridas es un muro de texto en
 * el que no se puede comparar dos costos ni encontrar un lote. En tabla, cada
 * dato cae bajo su columna y los números se alinean a la derecha, que es como
 * se lee una factura. Y va plegado: a la lista se entra a ver QUÉ compras hay,
 * no a leer el detalle de todas a la vez.
 */
function FilaCompra({
  compra: c,
  onConfirmar,
}: {
  compra: Compra;
  onConfirmar: () => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const unidades = c.items.reduce((s, i) => s + i.cantidad, 0);

  return (
    <li>
      <div className="flex flex-wrap items-start justify-between gap-3 px-6 py-4">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 font-medium text-slate-900">
            {c.proveedor_nombre ?? "Sin proveedor"}
            <Badge
              variant={
                c.estado === "CONFIRMADA"
                  ? "success"
                  : c.estado === "ANULADA"
                    ? "danger"
                    : "warning"
              }
            >
              {c.estado === "BORRADOR"
                ? "Borrador"
                : c.estado === "CONFIRMADA"
                  ? "Confirmada"
                  : "Anulada"}
            </Badge>
          </p>
          <p className="mt-0.5 text-xs text-slate-500">
            <span className="font-mono">
              {c.tipo_documento} {c.numero_documento ?? "—"}
            </span>
            {" · "}
            {c.fecha}
            {" · "}
            {c.sede_nombre ?? "—"}
            {c.registrado_por_nombre ? ` · ${c.registrado_por_nombre}` : ""}
          </p>
          <button
            type="button"
            onClick={() => setAbierta((v) => !v)}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-link)] hover:underline"
            aria-expanded={abierta}
          >
            {abierta ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {c.items.length} {c.items.length === 1 ? "producto" : "productos"} ·{" "}
            {unidades.toLocaleString("es-PE")} unidades
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-lg font-semibold text-slate-900">
            {soles(c.total)}
          </span>
          {c.estado === "BORRADOR" && (
            <Button onClick={onConfirmar}>
              <Check className="h-4 w-4" />
              Confirmar
            </Button>
          )}
        </div>
      </div>

      {abierta && (
        <div className="overflow-x-auto border-t border-slate-100 bg-slate-50/60 px-6 py-3">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-1.5 pr-4 font-medium">Producto</th>
                <th className="py-1.5 pr-4 font-medium">Lote</th>
                <th className="py-1.5 pr-4 font-medium">Vence</th>
                <th className="py-1.5 pr-4 text-right font-medium">Cantidad</th>
                <th className="py-1.5 pr-4 text-right font-medium">Costo unit.</th>
                <th className="py-1.5 text-right font-medium">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/70">
              {c.items.map((i) => (
                <tr key={i.id}>
                  <td className="py-1.5 pr-4 text-slate-900">{i.producto_nombre}</td>
                  <td className="py-1.5 pr-4 font-mono text-xs text-slate-500">
                    {i.numero_lote}
                  </td>
                  <td className="py-1.5 pr-4 font-mono text-xs text-slate-500">
                    {i.fecha_vencimiento ?? "—"}
                  </td>
                  <td className="py-1.5 pr-4 text-right font-mono">{i.cantidad}</td>
                  <td className="py-1.5 pr-4 text-right font-mono text-slate-600">
                    {soles(i.costo_unitario)}
                  </td>
                  <td className="py-1.5 text-right font-mono text-slate-900">
                    {soles(i.subtotal)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-300">
                <td className="py-1.5 pr-4 font-medium text-slate-700" colSpan={3}>
                  Total del ingreso
                </td>
                <td className="py-1.5 pr-4 text-right font-mono font-semibold">
                  {unidades.toLocaleString("es-PE")}
                </td>
                <td />
                <td className="py-1.5 text-right font-mono font-semibold text-slate-900">
                  {soles(c.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </li>
  );
}

function NuevaCompraDialog({
  onClose,
  onDone,
  onError,
}: {
  onClose: () => void;
  onDone: (msg: string) => void;
  onError: (m: string) => void;
}) {
  const { data: sedes = [] } = useQuery({ queryKey: ["sedes"], queryFn: () => listSedes() });
  const { data: proveedores = [] } = useQuery({
    queryKey: ["farmacia", "proveedores"],
    queryFn: () => listProveedores(),
  });
  const { data: productos = [] } = useQuery({
    queryKey: ["farmacia", "productos", ""],
    queryFn: () => listProductos({}),
  });
  const queryClient = useQueryClient();

  const [sedeId, setSedeId] = useState<number | "">("");
  const [proveedorId, setProveedorId] = useState<number | "">("");
  const [tipoDoc, setTipoDoc] = useState<"FACTURA" | "BOLETA" | "GUIA" | "OTRO">("FACTURA");
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [lineas, setLineas] = useState<LineaCompra[]>([]);
  const [nuevoProv, setNuevoProv] = useState("");

  const sedeEfectiva = sedeId === "" ? sedes[0]?.id : sedeId;
  const total = lineas.reduce((s, l) => s + l.cantidad * l.costo_unitario, 0);

  const crearProv = useMutation({
    mutationFn: () => createProveedor({ razon_social: nuevoProv }),
    onSuccess: (p) => {
      setNuevoProv("");
      setProveedorId(p.id);
      queryClient.invalidateQueries({ queryKey: ["farmacia", "proveedores"] });
    },
    onError: (e) => onError(formatApiError(e, "No se pudo crear el proveedor.")),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const payload: CompraCreate = {
        sede_id: sedeEfectiva as number,
        proveedor_id: proveedorId === "" ? null : proveedorId,
        tipo_documento: tipoDoc,
        numero_documento: numero || null,
        fecha,
        items: lineas.map((l) => ({
          producto_id: l.producto.id,
          numero_lote: l.numero_lote.trim() || "SIN-LOTE",
          fecha_vencimiento: l.fecha_vencimiento || null,
          cantidad: l.cantidad,
          costo_unitario: l.costo_unitario,
        })),
      };
      return createCompra(payload, true);
    },
    onSuccess: (c) => onDone(`Ingreso registrado por ${soles(c.total)}.`),
    onError: (e) => onError(formatApiError(e, "No se pudo registrar el ingreso.")),
  });

  function agregar(p: Producto) {
    setLineas([
      ...lineas,
      {
        producto: p,
        numero_lote: "",
        fecha_vencimiento: "",
        cantidad: 1,
        costo_unitario: 0,
      },
    ]);
  }

  function set(idx: number, campo: keyof LineaCompra, valor: unknown) {
    setLineas(lineas.map((l, i) => (i === idx ? { ...l, [campo]: valor } : l)));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Registrar ingreso de mercadería"
      description="Al guardar, el stock entra y queda registrado en el kardex."
      size="lg"
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Sede" htmlFor="c_sede">
            <select
              id="c_sede"
              value={sedeEfectiva ?? ""}
              onChange={(e) => setSedeId(Number(e.target.value))}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              {sedes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Proveedor" htmlFor="c_prov">
            <select
              id="c_prov"
              value={proveedorId}
              onChange={(e) =>
                setProveedorId(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Sin proveedor</option>
              {proveedores.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.razon_social}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Documento" htmlFor="c_tipo">
            <div className="flex gap-2">
              <select
                id="c_tipo"
                value={tipoDoc}
                onChange={(e) => setTipoDoc(e.target.value as typeof tipoDoc)}
                className="rounded-lg border border-slate-300 px-2 py-2 text-sm"
              >
                <option value="FACTURA">Factura</option>
                <option value="BOLETA">Boleta</option>
                <option value="GUIA">Guía</option>
                <option value="OTRO">Otro</option>
              </select>
              <Input
                value={numero}
                onChange={(e) => setNumero(e.target.value)}
                placeholder="F001-000123"
                className="font-mono"
              />
            </div>
          </Field>
          <Field label="Fecha" htmlFor="c_fecha">
            <Input
              id="c_fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </Field>
        </div>

        <div className="flex items-end gap-2">
          <Field label="Proveedor nuevo" htmlFor="c_nprov">
            <Input
              id="c_nprov"
              value={nuevoProv}
              onChange={(e) => setNuevoProv(e.target.value)}
              placeholder="Razón social de la droguería"
            />
          </Field>
          <Button
            variant="ghost"
            onClick={() => crearProv.mutate()}
            disabled={nuevoProv.trim().length < 2 || crearProv.isPending}
          >
            <Plus className="h-4 w-4" />
            Agregar
          </Button>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <Field label="Agregar producto" htmlFor="c_prod">
            <select
              id="c_prod"
              value=""
              onChange={(e) => {
                const p = productos.find((x) => x.id === Number(e.target.value));
                if (p) agregar(p);
              }}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Elegir producto…</option>
              {productos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} {p.concentracion ?? ""} ({p.codigo})
                </option>
              ))}
            </select>
          </Field>
        </div>

        {lineas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="py-1 font-medium">Producto</th>
                  <th className="py-1 font-medium">Lote</th>
                  <th className="py-1 font-medium">Vence</th>
                  <th className="py-1 font-medium">Cant.</th>
                  <th className="py-1 font-medium">Costo u.</th>
                  <th className="py-1 text-right font-medium">Subtotal</th>
                  <th className="py-1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {lineas.map((l, idx) => (
                  <tr key={`${l.producto.id}-${idx}`}>
                    <td className="py-1.5 pr-2 text-slate-800">{l.producto.nombre}</td>
                    <td className="py-1.5 pr-2">
                      <Input
                        value={l.numero_lote}
                        onChange={(e) => set(idx, "numero_lote", e.target.value)}
                        placeholder="SIN-LOTE"
                        className="w-28 font-mono"
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        type="date"
                        value={l.fecha_vencimiento}
                        onChange={(e) => set(idx, "fecha_vencimiento", e.target.value)}
                        className="w-36"
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        type="number"
                        min={1}
                        value={String(l.cantidad)}
                        onChange={(e) =>
                          set(idx, "cantidad", Math.max(1, Number(e.target.value) || 1))
                        }
                        className="w-20 font-mono"
                      />
                    </td>
                    <td className="py-1.5 pr-2">
                      <Input
                        type="number"
                        step="0.01"
                        value={String(l.costo_unitario)}
                        onChange={(e) =>
                          set(idx, "costo_unitario", Number(e.target.value) || 0)
                        }
                        className="w-24 font-mono"
                      />
                    </td>
                    <td className="py-1.5 text-right font-mono text-slate-700">
                      {soles(l.cantidad * l.costo_unitario)}
                    </td>
                    <td className="py-1.5 text-right">
                      <button
                        type="button"
                        onClick={() => setLineas(lineas.filter((_, i) => i !== idx))}
                        className="text-slate-400 hover:text-red-600"
                        aria-label="Quitar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400">Total</p>
            <p className="font-mono text-2xl font-bold text-slate-900">{soles(total)}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => guardar.mutate()}
              disabled={lineas.length === 0 || sedeEfectiva == null || guardar.isPending}
            >
              <Check className="h-4 w-4" />
              Guardar e ingresar al stock
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
